import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { lastValueFrom, map, Observable, shareReplay, Subject, takeUntil } from 'rxjs';
import { MarketingValidator } from '../../../../shared/validators/marketing.validator';
import { nipValidators } from '../../../../shared/validators/nip.validator';
import { CompanyAccountControllerService, CompanyService, CreateCompanyRequest, EditProfileRequest, InitializationCompanyAccountRequest } from '@mobiletract/account-service-angular-client';
import { patternIfFilledValidator } from '../../../../shared/validators/patternIfFilledValidator';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { parseApiError } from '../../../../shared/helpers/api-error.helper';

@Component({
  selector: 'app-first-configuration',
  standalone: false,
  templateUrl: './first-configuration.component.html'
})
export class FirstConfigurationComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  private readonly stepItemDefs = [
    { key: 'firstConfiguration.steps.welcome', icon: 'pi pi-fw pi-home' },
    { key: 'firstConfiguration.steps.basicData', icon: 'pi pi-fw pi-pencil' },
    { key: 'firstConfiguration.steps.addEntity', icon: 'pi pi-fw pi-id-card' },
    { key: 'firstConfiguration.steps.finish', icon: 'pi pi-fw pi-check' },
  ] as const;

  private breakpointObserver = inject(BreakpointObserver);
  private accountService = inject(CompanyAccountControllerService);
  private companyService = inject(CompanyService);
  private config = inject(DynamicDialogConfig);
  public readonly ref = inject(DynamicDialogRef);
  public translate = inject(TranslateService);

  returnUrl: string | undefined = this.config.data?.returnUrl;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay(1)
    );

  items: MenuItem[] = [];

  activeIndex = 0;

  accountFormError = '';
  accountFormLoading = false;

  credentialsFormError = '';
  credentialsForm!: FormGroup;
  credentialsFormLoading = false;

  readonly playStoreUrl = 'https://play.google.com/store/apps/details?id=com.mobiletract.bdo&utm_source=website&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1';

  marketingSource = [
    "Facebook",
    "Google",
    "Ekowitryna.pl",
    "Inne"
  ];

  private readonly postCodePattern = /^\d{2}-\d{3}$/;

  accountForm: FormGroup = new FormGroup({
    nipNumber: new FormControl('', nipValidators),
    companyName: new FormControl('', [Validators.required]),
    address: new FormControl('', []),
    postCode: new FormControl('', [
      Validators.pattern(this.postCodePattern),
      Validators.maxLength(6)
    ]),
    city: new FormControl('', []),
    marketingAgreement: new FormControl(false),
    marketingSource: new FormControl(this.marketingSource[0], [Validators.required]),
    marketingSourceOther: new FormControl(''),
  }, { validators: [MarketingValidator('marketingSource', 'marketingSourceOther')] });

  ngOnInit() {
    this.loadStepItems();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadStepItems());
    this.translate.onTranslationChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadStepItems());

    this.credentialsForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      nameFromBdo: new FormControl(false),
      clientId: new FormControl(null, [patternIfFilledValidator(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\s*$/)]),
      clientSecret: new FormControl(null, [patternIfFilledValidator(/^\s*[0-9a-f]{64}\s*$/)]),
      nip: new FormControl(null, nipValidators),
    });

    this.setupPostCodeFormatting();
    this.setupNipRestrict('nipNumber');
    this.setupNipRestrict('nip', this.credentialsForm);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadStepItems(): void {
    const keys = this.stepItemDefs.map(def => def.key);
    this.translate.get(keys).pipe(takeUntil(this.destroy$)).subscribe(translations => {
      this.items = this.stepItemDefs.map(def => ({
        label: translations[def.key],
        icon: def.icon,
      }));
    });
  }

  private setupPostCodeFormatting(): void {
    this.accountForm.get('postCode')?.valueChanges.subscribe((value: string) => {
      if (value == null || value === '') return;
      const digits = value.replace(/\D/g, '').slice(0, 5);
      const formatted = digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
      if (formatted !== value) {
        this.accountForm.get('postCode')?.setValue(formatted, { emitEvent: false });
      }
    });
  }

  private setupNipRestrict(controlName: string, form: FormGroup = this.accountForm): void {
    form.get(controlName)?.valueChanges.subscribe((value: string) => {
      if (value == null || value === '') return;
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits !== value) {
        form.get(controlName)?.setValue(digits, { emitEvent: false });
      }
    });
  }

  onNext() {
    if (this.activeIndex < this.items.length - 1) {
      this.activeIndex += 1;
    }
  }

  isRequired(formGroup: FormGroup, field: string): boolean {
    return !!formGroup.get(field)?.hasValidator(Validators.required);
  }

  getError(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.hasError('required')) {
      return this.translate.instant('firstConfiguration.errors.required');
    }

    if (field.hasError('marketingValidator')) {
      return this.translate.instant('firstConfiguration.errors.required');
    }

    if (field.hasError('minlength')) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return this.translate.instant('firstConfiguration.errors.minLength', { requiredLength });
    }

    if (field.hasError('pattern')) {
      if (fieldName === 'postCode') {
        return this.translate.instant('firstConfiguration.errors.postCodeInvalid');
      }
      if (fieldName === 'nipNumber' || fieldName === 'nip') {
        return this.translate.instant('firstConfiguration.errors.nipInvalid');
      }
      return this.translate.instant('firstConfiguration.errors.pattern');
    }

    if (field.hasError('nipChecksum') && (fieldName === 'nipNumber' || fieldName === 'nip')) {
      return this.translate.instant('firstConfiguration.errors.nipChecksum');
    }

    return '';
  }

  saveAccountForm() {
    this.accountFormError = '';
    this.accountFormLoading = true;
    this.initAccount();
  }

  private initAccount(): void {
    const request: InitializationCompanyAccountRequest = {
      companyName: this.accountForm.get('companyName')?.value,
      marketingAgreement: this.accountForm.get('marketingAgreement')?.value,
      referrerSource: this.accountForm.get('marketingSource')?.value,
      referrerSourceDescription: this.accountForm.get('marketingSourceOther')?.value
    };

    lastValueFrom(this.accountService.initCompanyAccount(request))
      .then(() => this.saveCompanyInvoice())
      .catch(err => {
        this.accountFormError = parseApiError(err, this.translate.instant('firstConfiguration.errors.unexpectedError'));
        this.accountFormLoading = false;
      });
  }

  private saveCompanyInvoice(): void {
    const request: EditProfileRequest = {
      companyName: this.accountForm.get('companyName')?.value,
      companyInvoice: {
        companyName: this.accountForm.get('companyName')?.value,
        nipNumber: this.accountForm.get('nipNumber')?.value,
        address: this.accountForm.get('address')?.value,
        postCode: this.accountForm.get('postCode')?.value,
        city: this.accountForm.get('city')?.value,
      }
    };

    lastValueFrom(this.accountService.editProfile(request))
      .then(() => {
        this.activeIndex = 2;
      })
      .catch(err => {
        this.accountFormError = parseApiError(err, this.translate.instant('firstConfiguration.errors.unexpectedError'));
      })
      .finally(() => {
        this.accountFormLoading = false;
      });
  }

  isAuto(): boolean {
    return this.credentialsForm.controls['nameFromBdo'].value;
  }

  onChangeNameFromBdo(event: CheckboxChangeEvent) {
    if (event.checked) {
      this.credentialsForm.controls['name'].clearValidators();
      this.credentialsForm.controls['name'].disable();

      this.credentialsForm.controls['clientId'].setValidators([Validators.required, patternIfFilledValidator(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\s*$/)]);
      this.credentialsForm.controls['clientSecret'].setValidators([Validators.required, patternIfFilledValidator(/^\s*[0-9a-f]{64}\s*$/)]);

      this.credentialsForm.controls['nip'].clearValidators();
      this.credentialsForm.controls['nip'].disable();
    } else {
      this.credentialsForm.controls['name'].setValidators([Validators.required, Validators.minLength(3)]);
      this.credentialsForm.controls['name'].enable();

      this.credentialsForm.controls['clientId'].setValidators([patternIfFilledValidator(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\s*$/)]);
      this.credentialsForm.controls['clientSecret'].setValidators([patternIfFilledValidator(/^\s*[0-9a-f]{64}\s*$/)]);

      this.credentialsForm.controls['nip'].setValidators(nipValidators);
      this.credentialsForm.controls['nip'].enable();
    }

    this.credentialsForm.controls['name'].updateValueAndValidity();
    this.credentialsForm.controls['clientId'].updateValueAndValidity();
    this.credentialsForm.controls['clientSecret'].updateValueAndValidity();
    this.credentialsForm.controls['nip'].updateValueAndValidity();
  }

  skipCredentialsForm() {
    this.activeIndex = 3;
    this.scrollToTop();
  }

  scrollToTop() {
    const dialogContentElement = document.querySelector('.p-dialog .p-dialog-content');
    if (dialogContentElement) {
      dialogContentElement.scrollTop = 0;
    }
  }

  saveCredentialsForm() {
    this.credentialsFormLoading = true;
    this.credentialsFormError = '';
    this.saveCredential(this.credentialsForm.getRawValue());
  }

  private saveCredential(data: any) {
    const isNameFromBdo = data.nameFromBdo;

    const request: CreateCompanyRequest = {
      name: isNameFromBdo ? undefined : data.name,
      companyNameFromBdo: isNameFromBdo,
      nip: isNameFromBdo ? undefined : data.nip,
      bdoClientId: data.clientId?.trim() || null,
      bdoClientSecret: data.clientSecret?.trim() || null
    };

    lastValueFrom(this.companyService.createCompany(request)).then(() => {
      this.credentialsFormLoading = false;
      this.activeIndex = 3;
      this.scrollToTop();
    })
      .catch(err => {
        this.credentialsFormLoading = false;
        this.credentialsFormError = parseApiError(err, this.translate.instant('firstConfiguration.errors.unexpectedError'));
      });
  }

  finish(): void {
    this.ref.close(this.returnUrl);
  }
}
